import { Sun, Moon } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { useTheme } from "@/lib/theme";
import { STAFF_STYLES, STAFF_DOT, ASSIGN_STYLES, AVAIL_STYLES } from "@/lib/format";

export const Logo = ({ light = false, compact = false }) => (
  <div className="flex items-center gap-3" data-testid="atlas-logo">
    <img src="/atlas-crest.webp" alt="Atlas" data-testid="atlas-logo-crest" className={`w-10 h-10 rounded-lg object-cover shrink-0 border ${light ? "border-[#C5A059]/40" : "border-[#C5A059]/70"}`} />
    {!compact && (
      <div className="leading-tight">
        <div className="font-display text-lg tracking-[0.25em] text-[#C5A059]">ATLAS</div>
        <div className="text-[9px] tracking-[0.3em] uppercase text-white/45 whitespace-nowrap">Event Services</div>
      </div>
    )}
  </div>
);

export const LangToggle = ({ light = false }) => {
  const { lang, setLang } = useLang();
  return (
    <div className="inline-flex rounded-full border border-white/15 p-0.5 text-[11px] font-semibold tracking-wider" data-testid="lang-toggle">
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`lang-${l}-btn`}
          onClick={() => setLang(l)}
          className={`press rounded-full px-2.5 py-1 uppercase ${lang === l ? "bg-[#C5A059] text-[#0A0A0C]" : "text-white/55 hover:text-white"}`}
        >
          {l}
        </button>
      ))}
    </div>
  );
};

export const ThemeToggle = () => {
  const { theme, toggle } = useTheme();
  const { t } = useLang();
  const Icon = theme === "light" ? Moon : Sun;
  const label = t(theme === "light" ? "theme.toDark" : "theme.toLight");
  return (
    <button data-testid="theme-toggle" data-theme={theme} onClick={toggle} title={label} aria-label={label}
      className="press w-8 h-8 rounded-full border border-white/15 grid place-items-center text-white/55 hover:text-[#C5A059] hover:border-[#C5A059]/60">
      <Icon className="w-3.5 h-3.5" strokeWidth={1.75} />
    </button>
  );
};

const Pill = ({ className, children, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1.5 border rounded-full px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${className}`}>
    {children}
  </span>
);

export const StaffBadge = ({ status, testid }) => {
  const { t } = useLang();
  return (
    <Pill className={STAFF_STYLES[status]} testid={testid}>
      <span className={`w-1.5 h-1.5 rounded-full ${STAFF_DOT[status]}`} />
      {t(`st.${status}`)}
    </Pill>
  );
};

export const AssignBadge = ({ status, testid }) => {
  const { t } = useLang();
  return <Pill className={ASSIGN_STYLES[status]} testid={testid}>{t(`as.${status}`)}</Pill>;
};

export const AvailBadge = ({ status, testid }) => {
  const { t } = useLang();
  if (!status) return <Pill className="text-neutral-400 border-dashed border-neutral-300" testid={testid}>{t("av.none")}</Pill>;
  return <Pill className={AVAIL_STYLES[status]} testid={testid}>{t(`av.${status}`)}</Pill>;
};

export const EventStatusBadge = ({ status, testid }) => {
  const { t } = useLang();
  const cls = { draft: "text-neutral-500 border-white/15", published: "text-[#D9B872] bg-[#C5A059]/10 border-[#C5A059]/40", cancelled: "text-red-700 bg-red-50 border-red-200" }[status];
  return <Pill className={cls} testid={testid}>{t(`es.${status}`)}</Pill>;
};

export const ProgressLine = ({ filled, required, testid }) => {
  const pct = required ? Math.min(100, Math.round((filled / required) * 100)) : 0;
  const color = pct >= 100 ? "bg-emerald-500" : pct === 0 ? "bg-red-400" : "bg-[#C5A059]";
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="h-1.5 flex-1 rounded-full bg-black/[0.06] overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%`, transition: "width 0.5s ease" }} />
      </div>
      <span className="mono text-xs text-neutral-600 whitespace-nowrap">{filled}/{required}</span>
    </div>
  );
};

export const ProgressRing = ({ filled, required, label, size = 112, testid }) => {
  const r = (size - 10) / 2;
  const c = 2 * Math.PI * r;
  const pct = required ? Math.min(1, filled / required) : 0;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }} data-testid={testid}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="var(--ring-track)" strokeWidth="6" fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke={pct >= 1 ? "#22C55E" : "#C5A059"} strokeWidth="6" fill="none" strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - pct)} style={{ transition: "stroke-dashoffset 0.6s ease" }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
        <div className="mono text-2xl leading-none"><span className="text-[#D9B872]">{filled}</span><span className="text-neutral-500 text-base"> / {required}</span></div>
        {label && <div className="text-[9px] uppercase tracking-wider text-neutral-500 mt-1 px-3 leading-tight">{label}</div>}
      </div>
    </div>
  );
};

const AVATAR_TONES = ["#C5A059", "#8B7355", "#6B8E7F", "#7C6F9C", "#9C6F6F", "#5F7C9C"];

export const Avatar = ({ name = "", size = 28, dim = false }) => {
  const initials = name.split(/[\s-]+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();
  const tone = AVATAR_TONES[[...name].reduce((s, ch) => s + ch.charCodeAt(0), 0) % AVATAR_TONES.length];
  return (
    <span className={`inline-grid place-items-center rounded-full shrink-0 font-semibold text-[#0A0A0C] ring-2 ring-[#131315] ${dim ? "opacity-40" : ""}`}
      style={{ width: size, height: size, background: tone, fontSize: size * 0.38 }}>{initials}</span>
  );
};

export const PageHeader = ({ eyebrow, title, sub, actions }) => (
  <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-8 rise">
    <div>
      {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
      <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight leading-none" data-testid="page-title">{title}</h1>
      {sub && <p className="mt-3 text-sm text-neutral-500 max-w-xl">{sub}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Empty = ({ text, testid }) => (
  <div className="card-lux p-10 text-center text-sm text-neutral-500" data-testid={testid}>{text}</div>
);
